import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { Action, State, StateContext } from "@ngxs/store";
import { uniq } from "lodash";
import { map } from "rxjs/operators";

import { LogModel } from "../../core/models";
import { TripReport } from "../../core/models/trip-report.model";
import { ApplicationService } from "../../core/services/application.service";
import { FileService } from "../../core/services/file.service";
import { ReportExportType } from "../../shared/enums/report-export-type.enum";
import { ReportType } from "../../shared/enums/report-type.enum";
import { generateRandomColor, updateEntities } from "../utils";
import { dispatchRequest } from "../dispatch-request";

import {
  ClearOldLogsAction,
  GenerateReportAction,
  GenerateReportFailAction,
  GenerateReportSuccessAction,
  LoadLastLogsAction,
  LoadLastLogsFailAction,
  LoadLastLogsSuccessAction,
  LoadVehicleLogsAction,
  LoadVehicleLogsFailAction,
  LoadVehicleLogsSuccessAction,
  ReportResponseItem,
  ResetLogStateAction,
  SetInactiveVehiclesLogsAction,
} from "./log.actions";
import {
  defaultRequestState,
  IRequestsNestedState,
} from "../interfaces/requests.interface";

export interface LogStateModel {
  entities: { [key: number]: LogModel };
  ids: number[];
  liveLogsIds: number[];
  lastLogsIds: number[];
  inactiveVehiclesLogsIds: number[];
  colors: { [key: number]: string };
  report: Array<{ tripDate: Date; items: TripReport[] }>;
  requests: {
    generateReport: IRequestsNestedState;
    getLastLogs: IRequestsNestedState;
  };
}

const defaultLogState = {
  entities: {},
  ids: [],
  liveLogsIds: [],
  lastLogsIds: [],
  inactiveVehiclesLogsIds: [],
  colors: null,
  report: null,
  requests: {
    generateReport: { ...defaultRequestState },
    getLastLogs: { ...defaultRequestState },
  },
};

// The live log history is capped per vehicle so the store does not grow for the
// life of the tab.
const MAX_LOGS_PER_VEHICLE = 25;

// Drops every live log beyond the newest MAX_LOGS_PER_VEHICLE for each vehicle.
// This used to be a separate ClearOldLogsAction dispatched after every poll,
// which meant a second state emission and so a second run of every consumer of
// the store - the live tracking marker pass and the vehicle filter table both
// ran twice per ten second poll. It also built its result with lodash omit()
// and difference() over the whole entity map, which is why it got expensive as
// the fleet grew.
function pruneLiveLogs(
  entities: { [key: number]: LogModel },
  ids: number[],
  liveLogsIds: number[]
): Pick<LogStateModel, "entities" | "ids" | "liveLogsIds"> {
  const idsByVehicle = new Map<number, number[]>();

  for (const id of liveLogsIds) {
    const log = entities[id];
    if (!log) {
      continue;
    }
    const group = idsByVehicle.get(log.logVehicleId);
    if (group) {
      group.push(id);
    } else {
      idsByVehicle.set(log.logVehicleId, [id]);
    }
  }

  const idsToRemove = new Set<number>();
  idsByVehicle.forEach((group) => {
    if (group.length <= MAX_LOGS_PER_VEHICLE) {
      return;
    }
    group
      .sort(
        (a, b) =>
          new Date(entities[b].logLocalDateTime).getTime() -
          new Date(entities[a].logLocalDateTime).getTime()
      )
      .slice(MAX_LOGS_PER_VEHICLE)
      .forEach((id) => idsToRemove.add(id));
  });

  if (!idsToRemove.size) {
    return { entities, ids, liveLogsIds };
  }

  const keptEntities: { [key: number]: LogModel } = {};
  for (const key in entities) {
    if (!idsToRemove.has(Number(key))) {
      keptEntities[key] = entities[key];
    }
  }

  return {
    entities: keptEntities,
    ids: ids.filter((id) => !idsToRemove.has(id)),
    liveLogsIds: liveLogsIds.filter((id) => !idsToRemove.has(id)),
  };
}

@State<LogStateModel>({
  name: "log",
  defaults: defaultLogState,
})
@Injectable()
export class LogState {
  constructor(
    private httpClient: HttpClient,
    private applicationService: ApplicationService,
    private fileService: FileService
  ) {}

  @Action(LoadLastLogsAction)
  loadLastLogs(ctx: StateContext<LogStateModel>) {
    const request = this.httpClient.get("GetLastPoints");

    // requests.getLastLogs has never been written and nothing reads it; leaving
    // it alone keeps the ten second poll from emitting two extra states a tick.
    return dispatchRequest(
      request,
      ctx,
      LoadLastLogsSuccessAction,
      LoadLastLogsFailAction
    );
  }

  @Action(LoadLastLogsSuccessAction)
  loadLastLogsSuccess(
    { getState, patchState }: StateContext<LogStateModel>,
    { payload }: LoadLastLogsSuccessAction
  ) {
    const state = getState();
    let colors = { ...state.colors };
    const stateUpdate = updateEntities(payload, LogModel);
    const newEntities = stateUpdate.entities;

    // if colors are not set then set them
    if (!colors) {
      colors = {};
      for (const key in newEntities) {
        if (newEntities[key]) {
          colors[newEntities[key].logVehicleId] = generateRandomColor();
        }
      }
    }

    // Merge and prune in one patch. Splitting the prune into its own action
    // made every poll emit twice.
    const pruned = pruneLiveLogs(
      { ...state.entities, ...stateUpdate.entities },
      uniq([...state.ids, ...stateUpdate.ids]),
      uniq([...state.liveLogsIds, ...stateUpdate.ids])
    );

    patchState({
      ...pruned,
      lastLogsIds: stateUpdate.ids,
      colors,
    });
  }

  @Action(LoadLastLogsFailAction)
  loadLastLogsFail() {
    console.log("LoadLastLogsFail");
  }

  @Action(LoadVehicleLogsAction)
  loadVehicleLogs(
    ctx: StateContext<LogStateModel>,
    { payload: filter }: LoadVehicleLogsAction
  ) {
    const fromdate = filter?.fromdate;
    const todate = filter?.todate;
    const take = filter?.take?.toString() || "1000";
    const vehicleId = filter?.vehicleId?.toString();
    const request = this.httpClient.get("GetLogs", {
      params: {
        ...(fromdate ? { fromdate } : {}),
        ...(todate ? { todate } : {}),
        ...(take ? { take } : {}),
        ...(vehicleId ? { vehicleId } : {}),
        take,
      },
    });

    return dispatchRequest(
      request,
      ctx,
      LoadVehicleLogsSuccessAction,
      LoadVehicleLogsFailAction
    );
  }

  @Action(LoadVehicleLogsSuccessAction)
  loadVehicleLogsSuccess(
    { getState, patchState }: StateContext<LogStateModel>,
    { payload }: LoadVehicleLogsSuccessAction
  ) {
    const state = getState();
    const stateUpdate = updateEntities(payload, LogModel);

    patchState({
      entities: {
        ...state.entities,
        ...stateUpdate.entities,
      },
      ids: [...state.ids, ...stateUpdate.ids],
    });
  }

  @Action(LoadVehicleLogsFailAction)
  loadVehicleLogsFail() {
    console.log("LoadVehicleLogsFail");
  }

  @Action(SetInactiveVehiclesLogsAction)
  setInactiveVehiclesLogs(
    { getState, patchState }: StateContext<LogStateModel>,
    { payload }: SetInactiveVehiclesLogsAction
  ) {
    const state = getState();
    const stateUpdate = updateEntities(payload, LogModel);
    patchState({
      entities: {
        ...state.entities,
        ...stateUpdate.entities,
      },
      ids: [...state.ids, ...stateUpdate.ids],
      inactiveVehiclesLogsIds: stateUpdate.ids,
    });
  }

  @Action(ClearOldLogsAction)
  clearOldLogs({ getState, patchState }: StateContext<LogStateModel>) {
    const state = getState();
    patchState(
      pruneLiveLogs(state.entities, state.ids, state.liveLogsIds)
    );
  }

  @Action(GenerateReportAction)
  generateReport(
    ctx: StateContext<LogStateModel>,
    { payload: { reportType, ...data } }: GenerateReportAction
  ) {
    let endpoint;

    this.applicationService.shownAppSpinner$.next(true);

    switch (true) {
      case reportType === ReportType.vehicleActivityReport &&
        data.exportType === ReportExportType.pdf:
        endpoint = "VehicleActivityReportData";
        break;
      case reportType === ReportType.vehiclePoiVisitsByDate &&
        data.exportType === ReportExportType.pdf:
        endpoint = "VehiclePOIVisitsByDateReportData";
        break;
      case reportType === ReportType.vehicleMovement &&
        data.exportType === ReportExportType.pdf:
        endpoint = "VehicleMovementReportData";
        break;
      case reportType === ReportType.vehicleExceptions &&
        data.exportType === ReportExportType.pdf:
        endpoint = "VehicleExceptionsReportData";
        break;
      case reportType === ReportType.fbtLogbook &&
        data.exportType === ReportExportType.pdf:
        endpoint = "FbtLogbookReportData";
        break;
      default:
        endpoint = "Report";
    }

    const options: any =
      data.exportType === ReportExportType.excel
        ? {
            observe: "response",
            responseType: "blob",
          }
        : {};

    let exportType = data.exportType;

    if (reportType == ReportType.fbtLogbook && data.vehicleId == null) {
      exportType = ReportExportType.excel;
    }

    const request = this.httpClient
      .post(`${endpoint}?type=${reportType}`, data, options)
      .pipe(
        map((response) => {
          const payload = {
            exportType: exportType,
            response,
            reportType,
          };

          return payload;
        })
      );

    return dispatchRequest(
      request,
      ctx,
      GenerateReportSuccessAction,
      GenerateReportFailAction,
      (generateReport, state) => ({
        requests: { ...state.requests, generateReport },
      })
    );
  }

  @Action(GenerateReportSuccessAction)
  generateReportSuccess(
    { patchState }: StateContext<LogStateModel>,
    {
      payload: { exportType, reportType, response },
    }: GenerateReportSuccessAction
  ) {
    const isCsvExportType = exportType === ReportExportType.excel;

    if (isCsvExportType) {
      this.fileService.saveCsvReport(response, reportType);
    }

    // reset current report if the csv report gets generated
    patchState({
      report: isCsvExportType
        ? null
        : // TODO validate this works
          (response as unknown as ReportResponseItem[]),
    });

    this.applicationService.shownAppSpinner$.next(false);
  }

  @Action(GenerateReportFailAction)
  generateReportFail() {
    this.applicationService.shownAppSpinner$.next(false);
  }

  @Action(ResetLogStateAction)
  resetLogState({ patchState }: StateContext<LogStateModel>) {
    patchState(defaultLogState);
  }
}
